import { Link } from "react-router-dom"

export default function PrivacyPage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Back navigation */}
        <Link 
          to="/" 
          className="inline-block mb-8 text-white/70 hover:text-white transition-colors"
        >
          ← Back to Home
        </Link>

        {/* Glassmorphism container */}
        <div className="rounded-3xl bg-white/5 backdrop-blur-lg border border-white/10 p-8 md:p-12 shadow-xl">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
          
          <p className="text-white/60 mb-8">
            Last updated: November 10, 2025
          </p>

          <div className="space-y-8 text-white/90">
            {/* Introduction */}
            <section>
              <p className="leading-relaxed">
                Welcome to Voy ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our AI-powered travel planning platform.
              </p>
            </section>

            {/* 1. Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">1.1 Information You Provide</h3>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li><strong>Account Information:</strong> Email address, name, password (encrypted)</li>
                <li><strong>Profile Information:</strong> Optional profile details, preferences, travel history</li>
                <li><strong>Travel Searches:</strong> Destinations, dates, preferences, search queries</li>
                <li><strong>Booking Information:</strong> Travel itineraries, hotel and flight selections</li>
                <li><strong>Payment Information:</strong> Payment details processed securely through third-party processors (we do not store full credit card numbers)</li>
              </ul>

              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">1.2 Information Automatically Collected</h3>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent, interactions</li>
                <li><strong>Device Information:</strong> Browser type, device type, operating system, IP address</li>
                <li><strong>Location Data:</strong> Approximate location for flight origin detection (with your permission)</li>
                <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
              </ul>

              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-6">1.3 Information from Third Parties</h3>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li><strong>OAuth Providers:</strong> Email, name, profile picture from Google, Apple (if you sign in with them)</li>
                <li><strong>Travel APIs:</strong> Hotel availability, flight options, pricing data from partners</li>
              </ul>
            </section>

            {/* 2. How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li>Provide and improve our AI-powered travel planning services</li>
                <li>Process your travel searches and bookings</li>
                <li>Personalize your experience and recommendations</li>
                <li>Communicate with you about your account, bookings, and updates</li>
                <li>Analyze usage patterns to improve our platform</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
                <li>Send marketing communications (with your consent)</li>
              </ul>
            </section>

            {/* 3. Data Storage and Security */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Data Storage and Security</h2>
              <p className="leading-relaxed mb-4">
                We use industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li><strong>Encryption:</strong> All data transmitted is encrypted using TLS/SSL</li>
                <li><strong>Secure Storage:</strong> Data stored on Supabase (PostgreSQL) with encryption at rest</li>
                <li><strong>Access Controls:</strong> Strict access controls and authentication</li>
                <li><strong>Password Security:</strong> Passwords are hashed using bcrypt</li>
                <li><strong>Regular Audits:</strong> Security reviews and updates</li>
              </ul>
            </section>

            {/* 4. Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Third-Party Services</h2>
              <p className="leading-relaxed mb-4">
                We work with trusted third-party services to provide our platform:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li><strong>Authentication:</strong> Supabase, Google OAuth, Apple Sign In</li>
                <li><strong>Analytics:</strong> Google Analytics</li>
                <li><strong>AI Services:</strong> Anthropic (Claude), OpenAI (GPT)</li>
                <li><strong>Travel Data:</strong> Duffel (flights), RateHawk (hotels)</li>
                <li><strong>Payment Processing:</strong> Stripe (payment details handled securely by Stripe)</li>
                <li><strong>Hosting:</strong> Netlify, Render</li>
              </ul>
              <p className="leading-relaxed mt-4">
                These services may collect and process data according to their own privacy policies. We carefully vet all partners for security and privacy compliance.
              </p>
            </section>

            {/* 5. Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Your Rights</h2>
              <p className="leading-relaxed mb-4">
                You have the following rights regarding your personal data:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Download your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing emails</li>
                <li><strong>Withdraw Consent:</strong> Revoke permissions for data processing</li>
              </ul>
              <p className="leading-relaxed mt-4">
                To exercise any of these rights, please contact us at <a href="mailto:social@voy.earth" className="text-primary hover:underline">social@voy.earth</a>
              </p>
            </section>

            {/* 6. Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Cookies and Tracking</h2>
              <p className="leading-relaxed mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li>Keep you signed in (session cookies)</li>
                <li>Remember your preferences (preference cookies)</li>
                <li>Analyze usage and improve our services (analytics cookies)</li>
                <li>Personalize your experience</li>
              </ul>
              <p className="leading-relaxed mt-4">
                You can control cookies through your browser settings. Disabling cookies may limit some functionality.
              </p>
            </section>

            {/* 7. Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention</h2>
              <p className="leading-relaxed">
                We retain your personal data only as long as necessary to provide our services and comply with legal obligations. When you delete your account, we permanently delete your personal data within 30 days, except where we're legally required to retain it longer.
              </p>
            </section>

            {/* 8. Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Children's Privacy</h2>
              <p className="leading-relaxed">
                Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            {/* 9. International Users */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. International Data Transfers</h2>
              <p className="leading-relaxed">
                Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this privacy policy and applicable laws (GDPR, CCPA, etc.).
              </p>
            </section>

            {/* 10. Changes to Privacy Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Changes to This Policy</h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any significant changes by email or through our platform. Your continued use after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* 11. Contact Us */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:
              </p>
              <div className="mt-4 p-4 rounded-2xl bg-white/10 border border-white/20">
                <p className="text-white/90">
                  <strong>Email:</strong> <a href="mailto:social@voy.earth" className="text-primary hover:underline">social@voy.earth</a><br />
                  <strong>Website:</strong> <a href="https://alpha.voy.earth" className="text-primary hover:underline">alpha.voy.earth</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

